import { useMemo, useState } from 'react';
import {Box, Cluster, Flex, Stack, WithIcon} from '../../shared/layouts';
import Card from '../../shared/Card';
import Header from '../../shared/Header';
import AuthIconFilled from 'imagesUwKluis/svg/shieldfill.svg';
import Table from '../../shared/Table';
import OpenIcon from 'imagesUwKluis/svg/open.svg';
import DisabledIcon from 'imagesUwKluis/svg/alertfill.svg';
import TrashIcon from 'imagesUwKluis/svg/trash.svg';
import TwoFactorAuthQrCodeModal from './TwoFactorAuthQrCodeModal';
import TwoFactorAuthActivateModal from './TwoFactorAuthActivateModal';
import TwoFactorAuthCompletedModal from './TwoFactorAuthCompletedModal';
import TwoFactorAuthModal from './TwoFactorAuthModal';
import axios from 'axios';
import RecoveryCodesInfoModal from './RecoveryCodesInfoModal';
import RecoveryCodesGenerateModal from './RecoveryCodesGenerateModal';
import Button from '../../shared/Button';
import Modal from '../../shared/Modal';
import {settingsDateFormatter} from '../../../utils/dateHelpers';
import {useTranslation} from "react-i18next";
import useIconLibrary from "../../../hooks/useIconLibrary";

export const authColumns = [
  {
    Header: '',
    accessor: 'auth_type',
    width: 300 * 0.33,
    disableSortBy: true,
  },
  {
    Header: '',
    accessor: 'setup_date',
    width: 300 * 0.33,
    disableSortBy: true,
  },
  {
    accessor: 'arrow',
    width: 300 * 0.1,
    disableSortBy: true,
    align: 'right',
  },
];

const enabledIcon = (
  <AuthIconFilled
    width="1.5rem"
    height="1.5rem"
    css={(theme) => ({ fill: theme?.colors?.succes || 'green' })}
  />
);

const disabledIcon = (
  <DisabledIcon
    width="1.5rem"
    height="1.5rem"
    css={(theme) => ({ fill: theme?.colors?.alertActive || 'red' })}
  />
);

const AuthSection = ({ data, refetchData, hideMfaTrashIcon }) => {
  const { t } = useTranslation();
  const [twoFaIsOpen, setTwoFaIsOpen] = useState(false);
  const [inAuthWizard, setinAuthWizard] = useState(false);
  function openTwoFaModal() {
    setTwoFaIsOpen(true);
  }

  const [twoFaQrCodeIsOpen, setTwoFaQrCodeIsOpen] = useState(false);
  function openTwoFaQrCodeModal() {
    setTwoFaIsOpen(false);
    setTwoFaQrCodeIsOpen(true);
  }

  const [twoFaActivateIsOpen, setTwoFaActivateIsOpen] = useState(false);
  function openTwoFaActivateModal() {
    setTwoFaIsOpen(false);
    setTwoFaQrCodeIsOpen(false);
    setTwoFaActivateIsOpen(true);
  }

  const [twoFaCompletedIsOpen, setTwoFaCompletedIsOpen] = useState(false);
  function openTwoFaCompletedModal() {
    setTwoFaIsOpen(false);
    setTwoFaQrCodeIsOpen(false);
    setTwoFaActivateIsOpen(false);
    setTwoFaCompletedIsOpen(true);
    setinAuthWizard(true);
  }

  const recoveryCodesInfoModalID = 'recovery-codes-info';
  const recoveryCodesGenerateModalID = 'recovery-codes-generate';
  const [openModal, setOpenModal] = useState(null);
  function modalOpen(modal) {
    if (inAuthWizard) setTwoFaCompletedIsOpen(false);
    setOpenModal(modal);
  }
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  let recoveryCodesData = null;
  if (data?.['recovery-codes']) {
    recoveryCodesData = data['recovery-codes'];
  }

  const rows = useMemo(() => {
    const array = [
      {
        auth_type: t('Authenticatie in twee stappen'),
        type: 'auth',
        enabled: data?.otp?.created_at !== undefined,
        setup_date: (
          <>
            <WithIcon space="s">
              {data?.otp?.created_at ? enabledIcon : disabledIcon}
              <span>
                {data?.otp?.created_at
                  ? t('Ingesteld op {{date}}', {date: settingsDateFormatter(new Date(data?.otp?.created_at))})
                  : t('Niet ingesteld')}
              </span>
            </WithIcon>
          </>
        ),
        arrow: hideMfaTrashIcon ? (
          <></>
        ) : data?.otp?.created_at ? (
          <TrashIcon
            width="1.5rem"
            height="1.5rem"
            css={(theme) => ({
              fill: theme?.colors?.secondaryFont || 'gray',
              cursor: 'pointer',
            })}
            onClick={() =>
              setConfirmModal({
                urls: [data?.otp?.delete, recoveryCodesData?.delete],
                isOpen: true,
                title: t('Weet je zeker dat je wilt ontkoppelen?'),
              })
            }
          />
        ) : (
          <OpenIcon
            width="1.5rem"
            height="1.5rem"
            css={(theme) => ({ fill: theme?.colors?.secondaryFont || 'gray' })}
          />
        ),
      },
    ];
    if (data?.otp?.created_at) {
      array.push({
        auth_type: t('Herstelcodes'),
        type: 'herstel',
        enabled: recoveryCodesData?.created_at !== undefined,
        setup_date: (
          <>
            <WithIcon space="s">
              {recoveryCodesData?.created_at ? enabledIcon : disabledIcon}
              <span>
                {recoveryCodesData?.created_at
                  ? t('Ingesteld op {{date}}', {
                      date: settingsDateFormatter(
                        new Date(recoveryCodesData?.created_at)
                      ),
                    })
                  : t('Niet ingesteld')}
              </span>
            </WithIcon>
          </>
        ),
        arrow: (
          <OpenIcon
            width="1.5rem"
            height="1.5rem"
            css={(theme) => ({ fill: theme?.colors?.secondaryFont || 'gray' })}
          />
        ),
      });
    }
    return array;
  }, [data]);

  const handleRowClick = (event) => {
    if (event.original.type === 'auth') {
      if (event.original.enabled) {
        return;
      }
      openTwoFaModal();
    }
    if (event.original.type === 'herstel') {
      if (event.original.enabled) {
        modalOpen('recovery-codes-generate');
      } else {
        modalOpen('recovery-codes-info');
      }
    }
  };

  const allEnabled = rows[0].enabled && rows[1].enabled;
  const { ShieldIcon } = useIconLibrary();

  return (
    <>
      <Modal
        isOpen={confirmModal.isOpen}
        showCloseButton={false}
        onRequestClose={() => setConfirmModal({ isOpen: false })}
        showProgressBar
        progressBarColor="alert"
      >
        <Stack space="3xl">
          <Box>{confirmModal.title}</Box>
          <Flex justifyContent="flex-end">
            <Cluster space="m">
              <Button
                onClick={() => setConfirmModal({ isOpen: false })}
                variant="alert-outline"
              >
                {t('Annuleren')}
              </Button>
              <Button
                onClick={() => {
                  Promise.all(
                    confirmModal.urls.map((url) => {
                      if (url) {
                        axios({
                          method: 'post',
                          url: url,
                          headers: { 'Content-Type': 'application/json' },
                        })
                      }
                    })
                  )
                    .then(() => {
                      refetchData();
                      setConfirmModal({ isOpen: false });
                    })
                    .catch((err) => console.error(err));
                }}
                variant="alert"
              >
                {t('Verwijderen')}
              </Button>
            </Cluster>
          </Flex>
        </Stack>
      </Modal>
      <TwoFactorAuthModal
        isOpen={twoFaIsOpen}
        onRequestClose={() => setTwoFaIsOpen(false)}
        onBtnClick={() => openTwoFaQrCodeModal()}
      />
      <TwoFactorAuthQrCodeModal
        isOpen={twoFaQrCodeIsOpen}
        onRequestClose={() => setTwoFaQrCodeIsOpen(false)}
        onBtnClick={() => openTwoFaActivateModal()}
      />
      <TwoFactorAuthActivateModal
        isOpen={twoFaActivateIsOpen}
        onRequestClose={() => setTwoFaActivateIsOpen(false)}
        onBtnClick={() => openTwoFaCompletedModal()}
      />
      <TwoFactorAuthCompletedModal
        isOpen={twoFaCompletedIsOpen}
        onBtnClick={() => modalOpen('recovery-codes-info')}
      />
      <RecoveryCodesInfoModal
        isOpen={openModal == recoveryCodesInfoModalID}
        onRequestClose={() => {
          if (inAuthWizard) return;
          setOpenModal(null);
        }}
        inAuthWizard={inAuthWizard}
        onBtnClick={() => modalOpen(recoveryCodesGenerateModalID)}
      />
      <RecoveryCodesGenerateModal
        isOpen={openModal == recoveryCodesGenerateModalID}
        onRequestClose={() => {
          refetchData();
          setOpenModal(null);
        }}
        onBtnClick={() => {
          refetchData();
          setOpenModal(null);
        }}
        inAuthWizard={inAuthWizard}
        hasRecoveryCodes={!!recoveryCodesData?.created_at}
      />
      <Card>
        <Stack space="m" width="100%">
          <Header
            title={t("Beveiliging")}
            icon={
              allEnabled ? (
                <AuthIconFilled height="2rem" width="2rem" />
              ) : (
                <ShieldIcon height="2rem" width="2rem" />
              )
            }
            iconColor={allEnabled ? 'succes' : 'primary'}
            variant="section"
          />
          <Box>
            {t('Stel authenticatie in twee stappen in om je account extra te beveiligen. Je logt dan in met een extra beveiligingscode ter controle van je identiteit.')}
            <br /><br />
            {t('Dankzij een herstelcode kan je ook inloggen als je geen toegang meer hebt tot je tweestapsauthenticatie codes.')}
          </Box>
          <Table
            columns={authColumns}
            data={rows}
            resetCursorCondition={(row) =>
              row.original.type === 'auth' && row.original.enabled
            }
            handleRowClick={(event) => handleRowClick(event)}
          />
        </Stack>
      </Card>
    </>
  );
};

export default AuthSection;
